/*
 * O provider e os hooks que o leem moram no mesmo arquivo de propósito: separá-los
 * deixaria um módulo com um `createContext` solto — exatamente o tipo de coisa que
 * alguém importa por engano e passa a ler um contexto vazio.
 */
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { PublicTenant } from '../lib/api-types';
import { queryKeys } from '../lib/query-client';
import { applyTenantIdentity } from '../theme/tenant-theme';

/**
 * A barbearia deste app.
 *
 * ---
 *
 * É A PRIMEIRA CHAMADA, E TUDO DEPENDE DELA
 *
 * Nome, logo, fuso, moeda e as regras de agendamento vêm daqui. Nada disso é
 * fixo no código: o mesmo build atende todas as barbearias, e é o HOST que diz
 * qual é qual. A COR não vem mais — os três apps usam a identidade visual da
 * plataforma desde a Etapa 14.
 *
 * Ainda assim o app não renderiza nada antes desta resposta. Mostrar uma casca
 * genérica e depois trocar o nome e o logo produziria um flash de identidade
 * errada — o cliente veria "outra barbearia" por um instante.
 */

interface TenantContextValue {
  readonly tenant: PublicTenant;
  /** Atalho para as funções de formatação, que precisam dos dois. */
  readonly locale: { locale: string; timeZone: string; currency: string };
}

const TenantContext = createContext<TenantContextValue | null>(null);

export function useTenant(): TenantContextValue {
  const value = useContext(TenantContext);

  if (!value) {
    throw new Error('useTenant() só funciona dentro de <TenantProvider>.');
  }

  return value;
}

export function useTenantQuery() {
  return useQuery({
    queryKey: queryKeys.tenant,
    queryFn: () => api.get<PublicTenant>('/public/tenant'),
    // A identidade da barbearia não muda durante uma sessão de uso. Refazer
    // esta chamada a cada foco da janela seria gastar rede para receber o mesmo
    // logo de volta.
    staleTime: 10 * 60_000,
    retry: 1,
  });
}

export function TenantProvider({
  tenant,
  children,
}: {
  tenant: PublicTenant;
  children: ReactNode;
}) {
  useEffect(() => {
    applyTenantIdentity({ name: tenant.name, branding: tenant.branding });
  }, [tenant]);

  return (
    <TenantContext.Provider
      value={{
        tenant,
        locale: {
          locale: tenant.locale,
          timeZone: tenant.timezone,
          currency: tenant.currency,
        },
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}
